import React, { useCallback, useMemo, useEffect } from 'react'
import PropTypes from 'prop-types'
import strings from 'utils/strings'
import { useSelector, useMakeSelector } from 'hooks/redux'
import { makeGetIsWaiting } from 'selectors/application'
import { useDispatch } from 'react-redux'
import { isEmpty, propOr, propEq } from 'ramda'
import * as yup from 'yup'
import {
  setDropdownSelectionByKey,
  createAndAddFilterTerm,
  handleRemoveFilterTerm,
  handleClearFilterTerms,
  clearDropdownByKey,
  ADS_SET_FILTER_DATA_BY_COLLECTION,
} from 'actions/search'
import {
  getDropdownSelectionSelector,
  getFiltersByCollection,
  selectAdvancedDropdownOptionsByGroup,
  selectFirstDropdownOptionsList,
} from 'selectors/search'
import {
  BASE_TYPE,
  FIRST_DROPDOWN,
  SECOND_DROPDOWN,
  THIRD_DROPDOWN,
  FOURTH_DROPDOWN,
  FIFTH_DROPDOWN,
  REFERENCE_PARAMETER,
} from 'utils/constants'
import ChipList from 'components/ChipList'
import { Paragraph } from 'components/patterns'
import {
  AdvancedSearchFiltersHeader,
  ClearFilters,
  StyledSelectMenu,
  AdvancedSearchArea,
  AdvancedSearchWrap,
  StyledLoading,
} from './AdvancedRPSearchFilters.style'

const collectionName = REFERENCE_PARAMETER

const AdvancedRPSearchFilters = ({ hideChips }) => {
  const dispatch = useDispatch()

  const loading = useMakeSelector(makeGetIsWaiting, {
    signals: [`${ADS_SET_FILTER_DATA_BY_COLLECTION}${collectionName}`],
  })
  const firstDropdownOptions = useSelector(selectFirstDropdownOptionsList, {
    collectionName,
    loading,
  })
  const firstDropdownSelection = useSelector(getDropdownSelectionSelector, {
    dropdownKey: FIRST_DROPDOWN,
    collectionName,
  })

  const secondDropdownOptions = useSelector(
    selectAdvancedDropdownOptionsByGroup,
    {
      collectionName,
      dropdownKey: SECOND_DROPDOWN,
      selection: firstDropdownSelection,
    }
  )
  const secondDropdownSelection = useSelector(getDropdownSelectionSelector, {
    dropdownKey: SECOND_DROPDOWN,
    collectionName,
  })

  const thirdDropdownOptions = useSelector(
    selectAdvancedDropdownOptionsByGroup,
    {
      collectionName,
      dropdownKey: THIRD_DROPDOWN,
      selection: secondDropdownSelection,
    }
  )
  const thirdDropdownSelection = useSelector(getDropdownSelectionSelector, {
    dropdownKey: THIRD_DROPDOWN,
    collectionName,
  })

  const fourthDropdownOptions = useSelector(
    selectAdvancedDropdownOptionsByGroup,
    {
      collectionName,
      dropdownKey: FOURTH_DROPDOWN,
      selection: thirdDropdownSelection,
    }
  )
  const fourthDropdownSelection = useSelector(getDropdownSelectionSelector, {
    dropdownKey: FOURTH_DROPDOWN,
    collectionName,
  })

  const fifthDropdownOptions = useSelector(
    selectAdvancedDropdownOptionsByGroup,
    {
      collectionName,
      dropdownKey: FIFTH_DROPDOWN,
      selection: fourthDropdownSelection,
    }
  )
  const fifthDropdownSelection = useSelector(getDropdownSelectionSelector, {
    dropdownKey: FIFTH_DROPDOWN,
    collectionName,
  })

  const activeFilters = useSelector(getFiltersByCollection, collectionName)

  const hasMultiFilter = propEq('id', BASE_TYPE)

  // dropdown change handlers
  const onFirstDropdownChange = useCallback(
    (selectedValue) => {
      dispatch(
        setDropdownSelectionByKey(selectedValue, collectionName, FIRST_DROPDOWN)
      )
      dispatch(clearDropdownByKey(collectionName, SECOND_DROPDOWN))
    },
    [dispatch]
  )

  const onSecondDropdownChange = useCallback(
    (secondSelectedValue) => {
      dispatch(
        setDropdownSelectionByKey(
          secondSelectedValue,
          collectionName,
          SECOND_DROPDOWN
        )
      )
      if (!hasMultiFilter(firstDropdownSelection)) {
        dispatch(createAndAddFilterTerm(collectionName, SECOND_DROPDOWN))
        // A value has to be set because the input is controlled
        dispatch(
          setDropdownSelectionByKey(
            secondSelectedValue,
            collectionName,
            SECOND_DROPDOWN
          )
        )
      }
      dispatch(clearDropdownByKey(collectionName, THIRD_DROPDOWN))
    },
    [dispatch, firstDropdownSelection, hasMultiFilter]
  )

  // we have to control and clear the input
  // if not a multi filter and second select has value, code need to clear it
  useEffect(() => {
    if (
      !hasMultiFilter(firstDropdownSelection) &&
      !isEmpty(secondDropdownSelection)
    ) {
      dispatch(clearDropdownByKey(collectionName, SECOND_DROPDOWN))
    }
  }, [
    secondDropdownSelection,
    firstDropdownSelection,
    dispatch,
    hasMultiFilter,
  ])

  const onThirdDropdownChange = useCallback(
    (thirdSelectedValue) => {
      dispatch(
        setDropdownSelectionByKey(
          thirdSelectedValue,
          collectionName,
          THIRD_DROPDOWN
        )
      )
      dispatch(clearDropdownByKey(collectionName, FOURTH_DROPDOWN))
      dispatch(clearDropdownByKey(collectionName, FIFTH_DROPDOWN))
    },
    [dispatch]
  )

  const onFourthDropdownChange = useCallback(
    (fourthSelectedValue) => {
      dispatch(
        setDropdownSelectionByKey(
          fourthSelectedValue,
          collectionName,
          FOURTH_DROPDOWN
        )
      )
      dispatch(clearDropdownByKey(collectionName, FIFTH_DROPDOWN))
    },
    [dispatch]
  )

  const onFifthDropdownChange = useCallback(
    (fifthSelectedValue) => {
      dispatch(
        setDropdownSelectionByKey(
          fifthSelectedValue,
          collectionName,
          FIFTH_DROPDOWN
        )
      )
      dispatch(createAndAddFilterTerm(collectionName, FIFTH_DROPDOWN))
    },
    [dispatch]
  )

  // clear filter or chip handlers
  const handleChipRemove = useCallback(
    (value) => {
      dispatch(handleRemoveFilterTerm(value, collectionName))
    },
    [dispatch]
  )
  const handleClearFiltersDefault = () => {
    dispatch(handleClearFilterTerms(collectionName))
  }

  const hasSecondDropdownOptions = !isEmpty(secondDropdownOptions)
  const hasThirdDropdownOptions = !isEmpty(thirdDropdownOptions)
  const hasFourthDropdownOptions = !isEmpty(fourthDropdownOptions)
  const hasFifthDropdownOptions = !isEmpty(fifthDropdownOptions)
  const getSelectedValue = propOr(null, 'selectedValue')

  const Loader = <StyledLoading contained={true} size="sm" />
  const Filters = (
    <>
      <div>
        <StyledSelectMenu
          key={FIRST_DROPDOWN}
          loading={loading}
          vertical
          value={getSelectedValue(firstDropdownSelection)}
          onChange={onFirstDropdownChange}
          name={FIRST_DROPDOWN}
          showSearch
          filterOption
          placeholder={strings.select_criteria}
          options={firstDropdownOptions}
          rules={yup.string().nullable()}
        />
        <StyledSelectMenu
          key={SECOND_DROPDOWN}
          loading={loading}
          disabled={!hasSecondDropdownOptions}
          vertical
          value={getSelectedValue(secondDropdownSelection)}
          onChange={onSecondDropdownChange}
          name={SECOND_DROPDOWN}
          showSearch
          filterOption
          placeholder={strings.select_value}
          options={secondDropdownOptions}
          rules={yup.string().nullable()}
        />
        {hasMultiFilter(firstDropdownSelection) && (
          <>
            <StyledSelectMenu
              key={THIRD_DROPDOWN}
              loading={loading}
              disabled={!hasThirdDropdownOptions}
              vertical
              value={getSelectedValue(thirdDropdownSelection)}
              onChange={onThirdDropdownChange}
              name={THIRD_DROPDOWN}
              showSearch
              filterOption
              placeholder={strings.select_sp}
              options={thirdDropdownOptions}
              rules={yup.string().nullable()}
            />
            <StyledSelectMenu
              key={FOURTH_DROPDOWN}
              loading={loading}
              disabled={!hasFourthDropdownOptions}
              vertical
              value={getSelectedValue(fourthDropdownSelection)}
              onChange={onFourthDropdownChange}
              name={FOURTH_DROPDOWN}
              showSearch
              filterOption
              placeholder={strings.select_criteria}
              options={fourthDropdownOptions}
              rules={yup.string().nullable()}
            />
            <StyledSelectMenu
              key={FIFTH_DROPDOWN}
              loading={loading}
              disabled={!hasFifthDropdownOptions}
              vertical
              value={getSelectedValue(fifthDropdownSelection)}
              onChange={onFifthDropdownChange}
              name={FIFTH_DROPDOWN}
              showSearch
              filterOption
              placeholder={strings.select_criteria}
              options={fifthDropdownOptions}
              rules={yup.string().nullable()}
            />
          </>
        )}
      </div>
      <div>
        {!hideChips && !isEmpty(activeFilters) && (
          <ChipList chips={activeFilters} remove={handleChipRemove} />
        )}
      </div>
    </>
  )
  const activeFiltersLength = useMemo(
    () => (!isEmpty(activeFilters) ? Object.values(activeFilters).length : -1),
    [activeFilters]
  )

  const advancedFiltersTitle = (
    <>
      {
        <Paragraph>{`${strings.advaned_filters} ${
          activeFiltersLength > -1 ? `(${activeFiltersLength})` : ``
        }`}</Paragraph>
      }
      {activeFiltersLength > -1 && (
        <ClearFilters onClick={handleClearFiltersDefault}>
          {strings.clear}
        </ClearFilters>
      )}
    </>
  )

  const FilterComponent = loading ? Loader : Filters

  return (
    <AdvancedSearchArea>
      <AdvancedSearchWrap>
        <AdvancedSearchFiltersHeader>
          {advancedFiltersTitle}
        </AdvancedSearchFiltersHeader>
        {FilterComponent}
      </AdvancedSearchWrap>
    </AdvancedSearchArea>
  )
}

export default AdvancedRPSearchFilters

AdvancedRPSearchFilters.propTypes = {
  hideChips: PropTypes.bool,
}
